if (!customElements.get("cart-upsell")) {
  class CartUpsell extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.template = null;
      this.slider = null;
      this.drawer = null;
      this.fetchAbort = null;
      this.cartUpdateEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) ||
        "cart-update";
      this.onCartUpdate = this.onCartUpdate.bind(this);
    }

    connectedCallback() {
      this.cacheElements();
      this.bindDrawerRefresh();
      document.addEventListener(this.cartUpdateEvent, this.onCartUpdate);
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener(this.cartUpdateEvent, this.onCartUpdate);
      if (this.fetchAbort) {
        this.fetchAbort.abort();
        this.fetchAbort = null;
      }
    }

    cacheElements() {
      this.track = this.querySelector("[data-upsell-track]");
      this.slider = this.querySelector("slider-component");
      this.drawer = this.closest("cart-drawer");
    }

    bindDrawerRefresh() {
      if (!this.drawer) return;
      if (typeof this.drawer.refreshUpsell === "function") return;
      this.drawer.refreshUpsell = () => {
        const upsell = this.drawer.querySelector("cart-upsell[data-upsell]");
        if (upsell && typeof upsell.refresh === "function") {
          upsell.refresh();
        }
      };
    }

    async refresh() {
      this.cacheElements();
      if (!this.track) return;
      const mode = this.dataset.upsellMode || "manual";
      if (mode !== "auto") return;

      const productId = this.dataset.upsellProductId;
      if (!productId) {
        this.classList.add("hidden");
        return;
      }

      const limit = Number.parseInt(this.dataset.upsellLimit || "3", 10) || 3;
      await this.loadRecommendations(productId, limit);
    }

    async onCartUpdate(event) {
      const variantId = event?.detail?.variantId;
      if (!variantId) return;
      await this.updateLatestProductId(variantId);
    }

    async updateLatestProductId(variantId) {
      try {
        const response = await fetch("/cart.js", { credentials: "same-origin" });
        if (!response.ok) return;
        const data = await response.json();
        const item = Array.isArray(data?.items)
          ? data.items.find(
              (entry) => String(entry.variant_id) === String(variantId)
            )
          : null;
        if (!item?.product_id) return;
        this.dataset.upsellProductId = String(item.product_id);
        const mode = this.dataset.upsellMode || "manual";
        if (mode === "auto") {
          await this.refresh();
        }
      } catch (error) {
        // noop
      }
    }

    async loadRecommendations(productId, limit) {
      if (!this.track) return;
      if (this.fetchAbort) {
        this.fetchAbort.abort();
      }
      const controller = new AbortController();
      this.fetchAbort = controller;
      try {
        const rawUrl = this.dataset.url || "";
        if (!rawUrl) throw new Error("Upsell URL missing.");
        const url = new URL(rawUrl, window.location.origin);
        url.searchParams.set("product_id", productId);
        url.searchParams.set("limit", String(limit));
        const sectionId =
          this.dataset.upsellSectionId || "cart-upsell-recommendations";
        url.searchParams.set("section_id", sectionId);
        const fetchHtml = window.webvista?.fetchHtml;
        const parsed = fetchHtml
          ? await fetchHtml(url.toString(), controller.signal)
          : new DOMParser().parseFromString(
              await fetch(url.toString(), { signal: controller.signal }).then(
                (response) => {
                  if (!response.ok) {
                    throw new Error("Upsell recommendations failed.");
                  }
                  return response.text();
                }
              ),
              "text/html"
            );
        const items = parsed.querySelector("[data-upsell-items]");
        if (!items || items.children.length === 0) {
          this.classList.add("hidden");
          return;
        }
        this.classList.remove("hidden");
        this.track.innerHTML = items.innerHTML;
        if (window.webvista?.initLazyImageAnimation) {
          window.webvista.initLazyImageAnimation(this.track);
        }
        this.resetSlider();
      } catch (error) {
        if (error?.name === "AbortError") return;
        this.classList.add("hidden");
      }
    }

    resetSlider() {
      if (!this.slider) return;
      if (typeof this.slider.disconnectedCallback === "function") {
        this.slider.disconnectedCallback();
      }
      if (typeof this.slider.connectedCallback === "function") {
        this.slider.connectedCallback();
      }
    }

  }

  customElements.define("cart-upsell", CartUpsell);
}
